import type { CSSProperties } from 'react'
import { WEB_ASSETS } from '@tenfour/contracts'
import styles from './ChromeIcon.module.css'

export type ChromeIconName = keyof typeof WEB_ASSETS.icons.chrome

interface ChromeIconProps {
  name: ChromeIconName
  size?: number
  className?: string
  /** Persistent sidebar controls use quiet gray artwork without a filled plate. */
  tone?: 'default' | 'muted'
}

interface ResponsiveAsset {
  oneX: string
  twoX: string
}

function ThemeAsset({
  asset,
  className,
}: {
  asset: ResponsiveAsset
  className: string
}) {
  return (
    <img
      src={asset.oneX}
      srcSet={`${asset.oneX} 1x, ${asset.twoX} 2x`}
      width={64}
      height={64}
      alt=""
      className={`${styles.asset} ${className}`}
      decoding="async"
      draggable={false}
    />
  )
}

/**
 * Responsive clay-relief artwork for recurring application chrome. The
 * surrounding control or nearby copy owns the accessible name; both theme
 * variants stay mounted so an explicit contractor theme change swaps without
 * duplicating theme state in JavaScript.
 */
export function ChromeIcon({
  name,
  size = 18,
  className,
  tone = 'default',
}: ChromeIconProps) {
  // Current New/add glyph from the design-system control symbol: a simple,
  // plate-free plus whose ink follows its button in both themes.
  if (name === 'add') return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
      <path d="M5 12h14M12 5v14" />
    </svg>
  )
  const muted = WEB_ASSETS.icons.chromeMuted
  const assets =
    tone === 'muted' && name in muted
      ? muted[name as keyof typeof muted]
      : WEB_ASSETS.icons.chrome[name]
  const style = { '--chrome-icon-size': `${size}px` } as CSSProperties

  return (
    <span
      className={[styles.icon, className].filter(Boolean).join(' ')}
      style={style}
      data-tone={tone}
      aria-hidden="true"
    >
      <ThemeAsset asset={assets.light} className={styles.light} />
      <ThemeAsset asset={assets.dark} className={styles.dark} />
    </span>
  )
}
